import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    Typography,
    Box,
} from '@material-ui/core';
import { Formik, Form } from 'formik';
import i18n from 'i18n-js';
import { IdName } from 'api/models';

interface Printer {
    address: string;
    workAreas: IdName[];
}

interface PrinterToWorkareaModalProps {
    open: boolean;
    onClose: () => void;
    printer: Printer;
    categories: IdName[];
    onSave: (workAreas: IdName[]) => void;
}

export function PrinterToWorkareaModal({
    open,
    onClose,
    printer,
    categories,
    onSave,
}: PrinterToWorkareaModalProps) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography variant="h6">{i18n.t('button.assignWorkAreas')}</Typography>
            </DialogTitle>

            <Formik
                initialValues={{
                    workAreas: printer.workAreas ?? [],
                }}
                enableReinitialize
                onSubmit={(values) => {
                    onSave(values.workAreas);
                    onClose();
                }}
            >
                {({ values, setFieldValue, dirty }) => (
                    <Form>
                        <DialogContent>
                            <Typography variant="body1">
                                {i18n.t('form.assignWorkAreasDescription')}
                            </Typography>

                            <FormControl variant="filled" fullWidth style={{ marginTop: 8 }}>
                                <InputLabel id="workareas-label">
                                    {i18n.t('restaurant.workAreas')}
                                </InputLabel>

                                <Select
                                    labelId="workareas-label"
                                    multiple
                                    value={values.workAreas.map((x) => x.id)}
                                    onChange={(e) => {
                                        const selectedIds = e.target.value as string[];

                                        const selectedWorkAreas = categories
                                            .filter((c) => selectedIds.includes(c.id))
                                            .map((c) => ({
                                                id: c.id,
                                                name: c.name,
                                            }));

                                        setFieldValue('workAreas', selectedWorkAreas);
                                    }}
                                    renderValue={(selected) =>
                                        categories
                                            .filter((c) => (selected as string[]).includes(c.id))
                                            .map((c) => c.name)
                                            .join(', ')
                                    }
                                >
                                    {categories.map((category) => (
                                        <MenuItem key={category.id} value={category.id}>
                                            <Checkbox
                                                checked={values.workAreas.some(
                                                    (wa) => wa.id === category.id
                                                )}
                                            />
                                            <ListItemText primary={category.name} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </DialogContent>

                        <Box mr={3}>
                            <DialogActions style={{ marginTop: 10 }}>
                                <Button onClick={onClose} color="primary">
                                    {i18n.t('button.cancel')}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    variant="contained"
                                    disabled={!dirty}
                                >
                                    {i18n.t('button.save')}
                                </Button>
                            </DialogActions>
                        </Box>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
}
